import type { SubscriptionUserTableType } from "./SubscriptionUserMgmtList";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Label } from "@components/index";
import { EmptyRow, TableBodyCell, TableHeaderCell } from "@components/table";
import { useIntersectionObserver } from "@customHooks/useIntersectionObserver";
import { getSortOrder } from "@utils/CommonUtil";
import type { TotalSubscriptionUserListModel } from "@repositories/subscriptionUserRepository/models/TotalSubscriptionUserListModel";

export const SubscriptionUserTable = ({
  subscriptionUserLists,
  scrollPackage,
  subscriptionUserFilterPackage,
}: SubscriptionUserTableType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [setLastSubscriptionList] = useIntersectionObserver({
    onIntersect: (entry) => {
      if (entry.isIntersecting) {
        scrollPackage.handleEndReached();
      }
    },
  });

  const handleChangeSort = (orderBy: string, order: SortOrder | undefined) => {
    subscriptionUserFilterPackage.setQueries((prevFilter) => ({
      ...prevFilter,
      sort: order ? [`${orderBy}.${order}`] : [],
    }));
  };

  const handleClickDetailInfo = (
    subscriptionUser: TotalSubscriptionUserListModel
  ) => {
    navigate(`${pathname}/${subscriptionUser.subscriptionId}`, {
      state: {
        id: subscriptionUser.subscriptionId,
        loginId: subscriptionUser.userLoginId,
        memberStatus: subscriptionUser.tenantMemberStatus,
        softwareIconUrl: subscriptionUser.softwareIconUrl,
        softwareName: subscriptionUser.softwareName,
        subscriptionName: subscriptionUser.subscriptionName,
      },
    });
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell
              width={284}
              height={42}
              orderBy="softwareName"
              order={getSortOrder(
                subscriptionUserFilterPackage.queries.sort,
                "softwareName"
              )}
              onSortChange={handleChangeSort}
            >
              {t("User_Management.Product_Name")}
            </TableHeaderCell>
            <TableHeaderCell
              width={360}
              height={42}
              orderBy="subscriptionName"
              order={getSortOrder(
                subscriptionUserFilterPackage.queries.sort,
                "subscriptionName"
              )}
              onSortChange={handleChangeSort}
            >
              {t("User_Management.Subscription_Name")}
            </TableHeaderCell>
            <TableHeaderCell
              width={240}
              height={42}
              orderBy="userName"
              order={getSortOrder(
                subscriptionUserFilterPackage.queries.sort,
                "userName"
              )}
              onSortChange={handleChangeSort}
            >
              {t("User_Management.User_Name")}
            </TableHeaderCell>
            <TableHeaderCell width={320} height={42}>
              {t("User_Management.Email_Account")}
            </TableHeaderCell>
            <TableHeaderCell
              width={140}
              height={42}
              orderBy="isAbnormal"
              order={getSortOrder(
                subscriptionUserFilterPackage.queries.sort,
                "isAbnormal"
              )}
              onSortChange={handleChangeSort}
            >
              {t("User_Management.Anomaly_Detection")}
            </TableHeaderCell>
            <TableHeaderCell
              width={200}
              height={42}
              orderBy="tenantMember"
              order={getSortOrder(
                subscriptionUserFilterPackage.queries.sort,
                "tenantMember"
              )}
              onSortChange={handleChangeSort}
            >
              {t("User_Management.User_Classification")}
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptionUserLists.length > 0 ? (
            subscriptionUserLists.map((subscriptionUser) => (
              <StyleTableRow
                key={subscriptionUser.userName}
                onClick={() => handleClickDetailInfo(subscriptionUser)}
              >
                <TableBodyCell>
                  <SoftwareLabel>
                    <SoftwareIcon src={subscriptionUser.softwareIconUrl} />
                    {subscriptionUser.softwareName}
                  </SoftwareLabel>
                </TableBodyCell>
                <TableBodyCell>
                  {subscriptionUser.subscriptionName}
                </TableBodyCell>
                <TableBodyCell>{subscriptionUser.userName}</TableBodyCell>
                <TableBodyCell>{subscriptionUser.userEmail}</TableBodyCell>
                <TableBodyCell>
                  <Label
                    variant="outline"
                    color={subscriptionUser.isAbnormal === "Y" ? "red" : "gray"}
                  >
                    {subscriptionUser.isAbnormal}
                  </Label>
                </TableBodyCell>
                <TableBodyCell>
                  <Label
                    variant="solid"
                    color={
                      subscriptionUser.tenantMemberStatus ===
                      t("Subscription.memberStatus.NORMAL_MEMBER")
                        ? "green"
                        : subscriptionUser.tenantMemberStatus ===
                          t("Subscription.memberStatus.WITHDRAW_MEMBER")
                        ? "red"
                        : "gray"
                    }
                  >
                    {subscriptionUser.tenantMemberStatus
                      ? subscriptionUser.tenantMemberStatus
                      : t("Subscription.memberStatus.NON_MEMBER_USER")}
                  </Label>
                </TableBodyCell>
              </StyleTableRow>
            ))
          ) : (
            <EmptyRow colSpan={6} title={t("Subscrib_Detail_User_29")} />
          )}
        </TableBody>
      </Table>
      {scrollPackage.hasNextPage && <div ref={setLastSubscriptionList} />}
    </TableContainer>
  );
};

const StyleTableRow = styled(TableRow)`
  cursor: pointer;
`;

export const SoftwareLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const SoftwareIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
`;
